import React, { useEffect, useMemo, useState, useCallback } from "react";
import axios from "axios";
import { Box, Button, IconButton, TextField, Typography } from "@mui/material";
import {
  GET_SITEOVERVIEW_REFRESH,
  POST_DOMAIN_KEYWORD,
  POST_DOMAIN_OVERVIEW,
} from "../../utils/services";
import { ZustandStore } from "../../../store/zustand-store";
import CustomTable from "../../../Mycomponent/share/customTable";
import globeImg from "../../../Mycomponent/Assets/rank-tracking-assets/www.svg";
import PopOver from "../../../Mycomponent/share/components/popover.component";
import CloseIcon from "@mui/icons-material/Close";
import "../../css/refreshData.scss";
import CountryBasedOnLanuage from "../../../Mycomponent/share/components/CountryBasedOnLanuage";
import AddProjectStore from "../../../store/AddProjectStore";

const SiteOverviewData = () => {
  const [siteOverviewData, setSiteOverviewData] = useState({
    data: [],
    loading: true,
    error: false,
  });
  const [domainName, setDomainName] = useState("");
  const [domainLocation, setDomainLocation] = useState({
    location_name: "worldwide",
    location_code: "worldwide",
    location_flag: globeImg,
    language_code: "",
    language_name: "",
    show: "worldwide",
  });
  const [loading, setLoading] = useState({});
  const [page, setPage] = React.useState(1);
  const { isAdminPerformAction, country_Language_Data } = ZustandStore(
    (state) => state
  );
  const showNotification = AddProjectStore((state) => state.showNotification);

  const fetchSiteOverviewData = async () => {
    try {
      const response = await axios.get(GET_SITEOVERVIEW_REFRESH());
      setSiteOverviewData({
        data: response.data,
        loading: false,
        error: false,
      });
    } catch (error) {
      showNotification({
        message: "api call fail",
        severity: "error",
      });
      setSiteOverviewData({
        data: [],
        loading: false,
        error: true,
      });
    }
  };

  useEffect(() => {
    fetchSiteOverviewData();
  }, []);

  const countryLanguageList = useMemo(() => {
    const worldWide = {
      title: "Worldwide",
      location_code: "Worldwide",
      location_name: "Worldwide",
      location_code_parent: null,
      country_iso_code: "ww",
      location_type: "Country",
      available_languages: [],
      flag: globeImg,
      available_sources: ["google"],
      language_name: "",
      language_code: "",
      keywords: 0,
      serps: 0,
    };
    return {
      ...country_Language_Data,
      data: [worldWide, ...(country_Language_Data?.data || [])],
    };
  }, [country_Language_Data]);

  const tableHeader = useMemo(() => {
    return [
      {
        label: "Domain",
        key: "domain",
        minWidth: 120,
        align: "left",
        width: "25%",
      },
      {
        label: "Location Name",
        key: "location_name",
        minWidth: 120,
        width: "25%",
        align: "center",
      },
      {
        label: "Last Updated",
        key: "last_update",
        minWidth: 120,
        align: "center",
        width: "25%",
      },
      {
        label: "",
        key: "space",
        minWidth: 120,
        align: "center",
        width: "25%",
        isAction: isAdminPerformAction?.isPerformAction,
      },
    ];
  }, [isAdminPerformAction?.isPerformAction]);

  const handleRefreshApi = async () => {
    setLoading((prev) => ({ ...prev, [domainName]: true }));
    try {
      const config = {
        domain_name: domainName,
        location_code: domainLocation.location_code,
        location_name: domainLocation.location_name,
        language_name: domainLocation.language_name,
      };
      // API calls using Promise.all
      const keywordPromise = axios.get(POST_DOMAIN_KEYWORD(config));
      const metricsPromise = axios.get(POST_DOMAIN_OVERVIEW(config));
      // Wait for both API calls to complete
      const [keywordResponse, metricsResponse] = await Promise.all([
        keywordPromise,
        metricsPromise,
      ]);
      showNotification({
        message: "API Call Finished: data refresh for" + domainName,
        severity: "success",
      });
    } catch (error) {
      showNotification({
        message: "API Call Failed: for" + domainName,
        severity: "error",
      });
    } finally {
      setLoading((prev) => ({ ...prev, [domainName]: false })); // Re-enable button
      setDomainName("");
      fetchSiteOverviewData();
    }
  };

  const handleSelectDomain = useCallback((item) => {
    setDomainName(item.domain);
  }, []);

  const tableData = useMemo(() => {
    return siteOverviewData?.data.map((item) => {
      return {
        domain: item?.domain,
        location_name: item?.location_name,
        last_update: (
          <span>{`${item.last_updated}/${item.Month}/${item.Year}`}</span>
        ),
        space: (
          <Button variant="contained" onClick={() => handleSelectDomain(item)}>
            Select
          </Button>
        ),
      };
    });
  }, [siteOverviewData, loading]);

  const countryDetailHandler = (e) => {
    setDomainLocation({
      location_name:
        e?.location_name === "Worldwide" ? "worldwide" : e?.location_name,
      location_code:
        e?.location_code === "Worldwide" ? "worldwide" : e?.location_code,
      location_flag: e?.flag,
      language_code: e?.language_code,
      language_name: e?.language_name,
      show: e?.location_name === "Worldwide" ? "worldwide" : "",
    });
  };

  return (
    <>
      <PopOver anchorEl={domainName !== ""}>
        <Box
          className="refresh_data_dialog"
          sx={{ minWidth: 300, minHeight: 400 }}
        >
          <Typography
            component="h1"
            className="heading-large font-bold popover-list-header py-2 px-2"
          >
            Site Overview Refresh
            <IconButton
              className="popover-close-icon"
              onClick={() => setDomainName("")}
              sx={{
                "&:hover": {
                  backgroundColor: "rgba(240, 242, 255, 1)",
                },
              }}
            >
              <CloseIcon sx={{ color: "black" }} fontSize="small" />
            </IconButton>
          </Typography>

          <Box
            className="d-flex align-items-center flex-wrap mb-3"
            style={{ gap: "10px" }}
          >
            <TextField
              id="standard-basic"
              variant="outlined"
              readOnly
              disabled
              value={domainName}
              sx={{
                // maxWidth: "230px",
                "& .MuiInputBase-input": {
                  padding: "14px",
                  height: "16px",
                },
              }}
            />

            <CountryBasedOnLanuage
              data={countryLanguageList}
              img={
                domainLocation?.show === "Worldwide"
                  ? globeImg
                  : domainLocation?.location_flag
              }
              title={
                domainLocation?.show === "Worldwide" ||
                domainLocation?.location_name === "worldwide"
                  ? "Worldwide"
                  : `${domainLocation?.language_name} / ${domainLocation?.location_name}`
              }
              callback={countryDetailHandler}
            />
          </Box>
          <Button
            variant="contained"
            onClick={handleRefreshApi}
            disabled={loading[domainName]} // Disable while API call is in progress
          >
            {loading[domainName] ? "Refreshing..." : "Refresh"}
          </Button>
        </Box>
      </PopOver>
      {siteOverviewData.error ? (
        <div>Error...</div>
      ) : (
        <CustomTable
          tableTitle="Site Overview"
          showSearchBar={true}
          rows={tableData}
          type="user_list"
          dataLoader={siteOverviewData.loading}
          tableHeader={tableHeader}
          page={page}
          setPage={setPage}
        />
      )}
    </>
  );
};

export default SiteOverviewData;
